import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Award, BookOpen, CheckCircle, XCircle, Loader2, LogOut, Repeat } from 'lucide-react';
import { useContext } from 'react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadingResults = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { setUser } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Çıkış yapılırken hata oluştu');
    }
  };
  
  useEffect(() => {
    loadSession();
  }, [sessionId]);
  
  const loadSession = async () => {
    try {
      const response = await axios.get(`${API}/reading/history`, {
        withCredentials: true
      });
      const foundSession = response.data.find(s => s.id === sessionId);
      if (foundSession) {
        setSession(foundSession);
      } else {
        toast.error('Sonuçlar bulunamadı');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Sonuçlar yüklenemedi');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Sonuçlar yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!session || !session.answers) return null;
  
  const correctCount = session.answers.filter(a => a.is_correct).length;
  const totalQuestions = session.answers.length;
  const percentage = ((correctCount / totalQuestions) * 100).toFixed(1);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Top Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/reading-test')}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg w-full sm:w-auto"
          >
            <Repeat className="w-5 h-5 mr-2" />
            Yeni Test Başlat 🎯
          </Button>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-gray-300 hover:bg-gray-50 font-semibold px-6 py-3 rounded-xl w-full sm:w-auto"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Geri
          </Button>
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-8">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl gradient-text">Reading Test Sonuçları</CardTitle>
            <p className="text-gray-600 mt-2">Seviye: {session.level}</p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Toplam Puan</p>
                <p className="text-4xl font-bold text-blue-600">{percentage}%</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Doğru Cevaplar</p>
                <p className="text-4xl font-bold text-green-600">{correctCount}</p>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                <XCircle className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Yanlış Cevaplar</p>
                <p className="text-4xl font-bold text-orange-600">{totalQuestions - correctCount}</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {session.answers.map((answer, index) => (
                <Card key={index} className={`border-2 ${
                  answer.is_correct ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                }`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Soru {index + 1}</CardTitle>
                      {answer.is_correct ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Soru:</p>
                      <p className="text-gray-800">{answer.question}</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className={`p-3 rounded ${
                        answer.is_correct ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
                      }`}>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Sizin Cevabınız:</p>
                        <p className="text-gray-800">{answer.user_answer}</p>
                      </div>
                      
                      {!answer.is_correct && (
                        <div className="p-3 rounded bg-green-100 border border-green-300">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Doğru Cevap:</p>
                          <p className="text-gray-800">{answer.correct_answer}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-center gap-4">
          <Button
            onClick={() => navigate('/exercises')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
          >
            Diğer Egzersizler 🎯
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReadingResults;